<template>
  <div class="modal-body">
    <h4 class="fw-bold mb-4 text-center">
      <i class="bi bi-play-circle-fill text-primary me-2"></i>
      Lessons
    </h4>

    <!-- LOADING -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary"></div>
      <p class="mt-3 text-muted">Loading lessons...</p>
    </div>

    <!-- LESSON CARDS -->
    <div v-else class="row g-4">
      <div
        class="col-xl-4 col-lg-4 col-md-6 col-sm-12"
        v-for="(video, index) in videos"
        :key="index"
      >
        <div class="card shadow-sm lesson-card h-100">
          <!-- EDIT ICON -->
          <div v-if="!video.editMode && video.id" class="edit-icon">
            <i class="bi bi-pencil-square" @click="toggleEdit(index)"></i>
          </div>

          <!-- VIEW MODE -->
          <div v-if="!video.editMode && video.video_id">
            <div class="ratio ratio-16x9">
              <iframe
                :src="`https://www.youtube.com/embed/${video.video_id}`"
                title="Lesson Video"
                allowfullscreen
              ></iframe>
            </div>

            <div class="card-body">
              <h6 class="fw-bold text-primary">
                <i class="bi bi-play-btn me-1"></i>
                {{ video.title }}
              </h6>

              <p class="text-muted small mb-0">
                {{ video.description }}
              </p>
            </div>
          </div>

          <!-- EDIT MODE -->
          <div v-else class="card-body">
            <!-- YOUTUBE LINK -->
            <div class="mb-2">
              <label class="form-label small">YouTube Video Link</label>
              <input
                type="text"
                class="form-control form-control-sm"
                placeholder="Paste YouTube link"
                v-model="video.video_link"
                @input="updateEmbed(video)"
              />
            </div>

            <div class="mb-2">
              <label class="form-label small">Title</label>
              <input
                type="text"
                class="form-control form-control-sm"
                v-model="video.title"
              />
            </div>

            <div class="mb-2">
              <label class="form-label small">Description</label>
              <textarea
                class="form-control form-control-sm"
                rows="2"
                v-model="video.description"
              ></textarea>
            </div>

            <div class="d-flex gap-2">
              <button
                class="btn btn-success btn-sm flex-fill"
                @click="saveVideo(index)"
                :disabled="!video.video_link || !video.title"
              >
                <i class="bi bi-check-circle me-1"></i>
                Save
              </button>

              <button
                class="btn btn-outline-secondary btn-sm flex-fill"
                @click="cancelEdit(index)"
              >
                <i class="bi bi-x-circle me-1"></i>
                Cancel
              </button>
            </div>
            <p v-if="video.errorMessage" class="text-danger small mt-2">
              {{ video.errorMessage }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      videos: [],
      loading: false,
    };
  },

  methods: {
    /* LOAD LESSONS */
    loadContent() {
      this.loading = true;

      axios
        .get("/lessons-content")
        .then((res) => {
          // Load existing lessons
          let loadedVideos = res.data.data.map((v) => ({
            ...v,
            video_link: "", // user will paste link if needed
            editMode: false,
            errorMessage: "",
          }));

          // Fill empty cards to always show 6 cards
          const maxCards = 6;
          while (loadedVideos.length < maxCards) {
            loadedVideos.push({
              id: null,
              video_id: null,
              video_link: "",
              title: "",
              description: "",
              editMode: true, // empty cards start in edit mode
              errorMessage: "",
            });
          }

          this.videos = loadedVideos;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },

    /* EXTRACT YOUTUBE ID */
    extractYoutubeId(url) {
      const regExp = /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/;
      const match = url.match(regExp);
      return match ? match[1] : null;
    },

    /* UPDATE EMBED WHEN USER PASTES LINK */
    updateEmbed(video) {
      const id = this.extractYoutubeId(video.video_link);
      video.video_id = id || null;
    },

    toggleEdit(index) {
      this.videos[index].editMode = true;
    },

    cancelEdit(index) {
      const video = this.videos[index];
      if (!video.id) {
        // reset empty card
        video.title = "";
        video.description = "";
        video.video_link = "";
        video.video_id = null;
      } else {
        video.editMode = false;
      }
      video.errorMessage = "";
    },

    async saveVideo(index) {
      const video = this.videos[index];
      video.errorMessage = "";

      if (!video.video_link || !video.title) {
        video.errorMessage = "Video link and title are required.";
        return;
      }

      const videoId = this.extractYoutubeId(video.video_link);

      if (!videoId) {
        video.errorMessage = "Invalid YouTube URL.";
        return;
      }

      try {
        const res = await axios.post("/save-lessons-content", {
          title: video.title,
          description: video.description,
          video_id: videoId, // send only ID
        });

        video.video_id = res.data.data.video_id;
        video.id = res.data.data.id;
        video.editMode = false;
      } catch (err) {
        video.errorMessage = "Failed to save video.";
      }
    },
  },

  mounted() {
    this.loadContent();
  },
};
</script>

<style scoped>
.lesson-card {
  position: relative;
  border-radius: 10px;
  overflow: hidden;
}

/* Edit icon */
.edit-icon {
  position: absolute;
  top: 10px;
  right: 12px;
  z-index: 10;
}

.edit-icon i {
  font-size: 18px;
  color: #0d6efd;
  cursor: pointer;
  background: white;
  padding: 4px 6px;
  border-radius: 5px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.edit-icon i:hover {
  color: #dc3545;
  transform: scale(1.1);
}
</style>
